// app/javascript/components/ShippingFlow/utils/formSignature.js

/**
 * Generates a deterministic hash signature from form data to detect meaningful changes
 * that would affect cart state (pricing, products, addresses, etc.)
 * 
 * @param {Object} formData - The form data from the store
 * @returns {Promise<string>} SHA-256 hash of essential form data
 */
export const generateFormSignature = async (formData) => {
  // Extract only the essential elements that affect cart state
  const essentialData = {
    // Traveler info
    traveler: {
      name: formData.traveler?.name || '',
      email: formData.traveler?.email || '',
      phone: formData.traveler?.phoneNumber || '',
    },
    
    // Coupon code
    couponCode: formData.couponCode || null,
    
    // Legs data
    legs: (formData.legs || []).map(leg => ({
      legIndex: leg.legIndex,
      
      // From address essentials
      fromAddress: {
        street: leg.fromAddress?.street,
        city: leg.fromAddress?.city,
        state: leg.fromAddress?.state,
        zip: leg.fromAddress?.zip,
        country: leg.fromAddress?.country,
        itineraryId: leg.fromAddress?.selectedItinerary?.id,
        stateroom: leg.fromAddress?.stateroom,
      },
      
      // To address essentials
      toAddress: {
        street: leg.toAddress?.street,
        city: leg.toAddress?.city,
        state: leg.toAddress?.state,
        zip: leg.toAddress?.zip,
        country: leg.toAddress?.country,
        itineraryId: leg.toAddress?.selectedItinerary?.id,
        stateroom: leg.toAddress?.stateroom,
      },
      
      // Service essentials
      service: {
        id: leg.selectedService?.id,
        shipDate: leg.selectedService?.ship_date,
        arrivalDate: leg.selectedService?.arrival_date,
      },
      
      // Products
      products: (leg.selectedProducts || []).map(product => ({
        productId: product.product_id || product.id,
        quantity: product.quantity || 1,
        productLine: product.product_line,
        packagingId: product.packaging?._id,
        packagingName: product.packaging?.name,
        insuranceId: product.insurance?.id,
      })).sort((a, b) => {
        const aId = a.productId || '';
        const bId = b.productId || '';
        return aId.localeCompare(bId);
      }), // Sort for consistency
      
      // Pickup fee
      pickupFee: leg.selectedPickupFee ? {
        id: leg.selectedPickupFee.id,
      } : null,
    })),
  };

  // Convert to deterministic JSON string (sorted keys for consistency)
  const jsonString = JSON.stringify(essentialData, Object.keys(essentialData).sort());
  
  // Generate SHA-256 hash using browser's SubtleCrypto API
  const encoder = new TextEncoder();
  const data = encoder.encode(jsonString);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
};

/**
 * Compare two form data objects to check if cart-relevant changes occurred
 * 
 * @param {Object} formData1 - First form data object
 * @param {Object} formData2 - Second form data object
 * @returns {Promise<boolean>} True if form data has changed in a way that affects cart
 */
export const hasFormDataChanged = async (formData1, formData2) => {
  if (!formData1 || !formData2) return true;
  
  const signature1 = await generateFormSignature(formData1);
  const signature2 = await generateFormSignature(formData2);
  
  return signature1 !== signature2;
};

/**
 * Hook-like helper to track form changes (can be used in components)
 * Usage: 
 *   const lastSignature = useRef(null);
 *   const shouldUpdateCart = await formHasChanged(formData, lastSignatureRef);
 * 
 * @param {Object} formData - Current form data
 * @param {Object} lastSignatureRef - React ref object to store last signature
 * @returns {Promise<boolean>} True if form has changed since last check
 */
export const formHasChanged = async (formData, lastSignatureRef) => {
  const currentSignature = await generateFormSignature(formData);
  
  if (!lastSignatureRef.current) {
    lastSignatureRef.current = currentSignature;
    return true; // First time, consider it changed
  }
  
  const hasChanged = lastSignatureRef.current !== currentSignature;
  
  if (hasChanged) {
    lastSignatureRef.current = currentSignature;
  }
  
  return hasChanged;
};